import { useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { useTenant } from "../../../../hooks/useTenant";
import { toast } from "react-toastify";
import { BUSINESS_RULES_DEFINITIONS } from "../../../../models/businessRule";

/**
 * useBusinessRules
 * Hook para carregar e salvar as regras de negócio do tenant.
 * Os dados ficam em: tenants/{idTenant} → campo "businessRules"
 */
export const useBusinessRules = () => {
    const { idTenant, user } = useTenant();
    const [rules, setRules] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // ── Carregar regras do tenant ──
    const loadRules = useCallback(async () => {
        if (!idTenant) return;
        setIsLoading(true);
        try {
            const tenantRef = doc(db, "tenants", idTenant);
            const tenantSnap = await getDoc(tenantRef);

            if (tenantSnap.exists()) {
                const data = tenantSnap.data();
                const savedRules = data.businessRules || {};

                // Merge com defaults
                const merged = {};
                BUSINESS_RULES_DEFINITIONS.forEach(rule => {
                    merged[rule.id] = savedRules[rule.id] !== undefined
                        ? savedRules[rule.id]
                        : rule.defaultValue;
                });
                setRules(merged);
            } else {
                // Usar defaults
                const defaults = {};
                BUSINESS_RULES_DEFINITIONS.forEach(rule => {
                    defaults[rule.id] = rule.defaultValue;
                });
                setRules(defaults);
            }
        } catch (err) {
            console.error("[useBusinessRules] Erro ao carregar regras:", err);
            toast.error("Erro ao carregar regras de negócio.");
        } finally {
            setIsLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        loadRules();
    }, [loadRules]);

    // ── Salvar regras ──
    const saveRules = useCallback(async (updatedRules) => {
        if (!idTenant) return;
        setIsSaving(true);
        try {
            const tenantRef = doc(db, "tenants", idTenant);
            await setDoc(tenantRef, {
                businessRules: updatedRules,
                updatedAt: serverTimestamp(),
                updatedBy: {
                    uid: user?.uid || "system",
                    name: user?.displayName || user?.fullName || user?.email || "Sistema"
                }
            }, { merge: true });

            setRules(updatedRules);
            toast.success("Regras de negócio salvas com sucesso!");
        } catch (err) {
            console.error("[useBusinessRules] Erro ao salvar regras:", err);
            toast.error("Erro ao salvar regras de negócio.");
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user]);

    return {
        rules,
        isLoading,
        isSaving,
        saveRules,
        isReady: !isLoading,
    };
};
